import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FolderLock, Upload, Download, Trash2, CheckCircle2, AlertTriangle,
  XCircle, FileUp, Loader2, Plus, Info, Calendar, Utensils, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";
import { Link } from "react-router-dom";
import AutoTransitionsPanel from "@/pages/dashboard/AutoTransitionsPanel";

const STATUS_META = {
  ok:       { tone: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2, label: "Current" },
  expiring: { tone: "bg-amber-50 border-amber-200 text-amber-800",       Icon: AlertTriangle, label: "Expiring soon" },
  expired:  { tone: "bg-red-50 border-red-200 text-red-800",             Icon: XCircle,       label: "Expired" },
  missing:  { tone: "bg-stone-100 border-stone-300 text-stone-600",      Icon: AlertTriangle, label: "Missing" },
};

export default function LicensingDocuments() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "admin";
  const [checklist, setChecklist] = useState([]);
  const [byKey, setByKey] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadFor, setUploadFor] = useState(null);  // {key,label,cadence}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/center/licensing/documents");
      setChecklist(res.data.items || []);
      setByKey(res.data.by_key || {});
      setDocuments(res.data.documents || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const countByStatus = (s) =>
    checklist.filter(i => i.required && (byKey[i.key]?.status || "missing") === s).length;

  /** iter-51 — Open a vault document in a new tab. Previously the View
   * button just did `window.open(/api/.../download)` which fails because
   * the backend requires a JWT bearer token (URL-only auth wasn't
   * enabled).  We now fetch the file with the auth header, turn it into a
   * blob URL, and open that. Falls back to triggering a download if the
   * browser blocks the popup. */
  const viewDocument = async (doc) => {
    try {
      const res = await api.get(`/center/licensing/documents/${doc.id}/download`, { responseType: "blob" });
      const mime = res.headers?.["content-type"] || doc.mime_type || "application/octet-stream";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.original_filename || doc.filename || "licensing-doc";
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const summary = [
    { key: "expired", label: "Expired", Icon: XCircle, color: "text-red-600" },
    { key: "missing", label: "Missing", Icon: AlertTriangle, color: "text-stone-500" },
    { key: "expiring", label: "Expiring in 30d", Icon: AlertTriangle, color: "text-amber-600" },
    { key: "ok", label: "Current", Icon: CheckCircle2, color: "text-emerald-700" },
  ];

  return (
    <section className="max-w-6xl mx-auto p-6 space-y-8" data-testid="licensing-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700">
            <FolderLock className="w-4 h-4" /> Center licensing file
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-medium text-stone-900 tracking-tight">
            Every operating document. One folder.
          </h1>
          <p className="mt-2 text-sm text-stone-600 max-w-2xl leading-relaxed">
            Upload Fire Drills, Health Inspections, Insurance, Occupancy permits and every other
            document a licensed Ohio childcare center needs on hand. We'll track each expiry date
            and flag anything overdue in the nightly digest.
          </p>
        </div>
        {isManager && (
          <Button
            data-testid="licensing-upload-btn"
            onClick={() => setUploadFor({ key: "", label: "Other document", cadence: "" })}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11"
          >
            <Upload className="w-4 h-4 mr-1" /> Upload document
          </Button>
        )}
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {summary.map(s => {
          const n = countByStatus(s.key);
          return (
            <div key={s.key} data-testid={`licensing-count-${s.key}`}
                 className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-stone-500">
                <s.Icon className={`w-3.5 h-3.5 ${s.color}`} /> {s.label}
              </div>
              <div className={`mt-2 font-display text-4xl font-light ${s.color}`}>{n}</div>
            </div>
          );
        })}
      </div>

      {/* iter-69c — Auto Classroom Transitions panel moved here from the
          Compliance page so the licensing-file workflow (uploads + auto
          DCY 01229 letter generation) lives in one place. */}
      <AutoTransitionsPanel />

      {/* iter-81t — Monthly Food Menu generator (Licensing binder) */}
      <Link
        to="/dashboard/licensing/food-menu"
        data-testid="licensing-food-menu-card"
        className="group block rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white hover:from-emerald-100 hover:border-emerald-400 p-5 transition"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center shrink-0">
            <Utensils className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-800">Monthly Food Menu · OAC 5101:2-12-19</div>
            <div className="font-medium text-stone-900 mt-0.5">Generate a landscape menu for the licensing binder</div>
            <div className="text-xs text-stone-500 mt-1">
              4-week catalog · Infant (6-11 mo) + Child (12+ mo) · 5- or 7-day operations · allergen notes · WIC/CACFP footer
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-emerald-700 group-hover:translate-x-1 transition" />
        </div>
      </Link>

      {loading ? (
        <div className="py-20 text-center text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" /> Loading licensing file…
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="licensing-list">
          {checklist.map(item => {
            const doc = byKey[item.key];
            const status = doc?.status || "missing";
            const meta = STATUS_META[status];
            return (
              <div key={item.key} data-testid={`licensing-row-${item.key}`}
                   className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full border px-2 py-0.5 inline-flex items-center gap-1 ${meta.tone}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {item.required && (
                      <span className="text-[10px] uppercase font-semibold tracking-wider rounded-full border border-stone-300 bg-stone-50 text-stone-600 px-2 py-0.5">
                        Required
                      </span>
                    )}
                    <span className="text-[11px] text-stone-500">{item.cadence}</span>
                  </div>
                  <div className="mt-1 font-medium text-stone-900">{item.label}</div>
                  {doc && (
                    <div className="mt-1 text-xs text-stone-500 space-x-2">
                      <span>Uploaded {fmtDate(doc.uploaded_at)}</span>
                      {doc.expires_on && <span>· Expires {fmtDate(doc.expires_on)}</span>}
                      {doc.notes && <span>· {doc.notes}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {doc ? (
                    <>
                      <Button
                        data-testid={`licensing-view-${item.key}`}
                        onClick={() => viewDocument(doc)}
                        variant="outline" className="rounded-xl h-9"
                      >
                        <Download className="w-4 h-4 mr-1" /> View / Print
                      </Button>
                      {isManager && (
                        <>
                          <Button
                            data-testid={`licensing-replace-${item.key}`}
                            onClick={() => setUploadFor({ key: item.key, label: item.label, cadence: item.cadence, existing: doc })}
                            variant="outline" className="rounded-xl h-9"
                          >
                            <FileUp className="w-4 h-4 mr-1" /> Replace
                          </Button>
                          <Button
                            data-testid={`licensing-delete-${item.key}`}
                            onClick={async () => {
                              if (!window.confirm(`Delete "${item.label}"?`)) return;
                              try { await api.delete(`/center/licensing/documents/${doc.id}`); toast.success("Deleted"); load(); }
                              catch (err) { toast.error(formatApiError(err)); }
                            }}
                            variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl h-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    isManager && (
                      <Button
                        data-testid={`licensing-upload-${item.key}`}
                        onClick={() => setUploadFor({ key: item.key, label: item.label, cadence: item.cadence })}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-9"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Upload
                      </Button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!uploadFor} onOpenChange={(v) => !v && setUploadFor(null)}>
        <DialogContent className="max-w-md" data-testid="licensing-upload-dialog">
          {uploadFor && (
            <>
              <DialogHeader>
                <DialogTitle>Upload {uploadFor.label}</DialogTitle>
                <DialogDescription>
                  Drop a PDF or image and we'll auto-detect the document type and expiry date. You can always override before uploading.
                </DialogDescription>
              </DialogHeader>
              <LicensingUploadForm
                checklist={checklist}
                preset={uploadFor}
                onDone={() => { setUploadFor(null); load(); }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function LicensingUploadForm({ checklist, preset, onDone }) {
  const [title, setTitle] = useState(preset.label || "");
  const [licensingKey, setLicensingKey] = useState(preset.key || "");
  const [expires, setExpires] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);  // {confidence, reasoning}
  const fileRef = useRef(null);

  // When the user drops a file, call /analyze first and pre-fill the form.
  // The user can still override everything — AI is a suggestion, not a lock.
  const onFilePicked = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setAnalysing(true);
    setAiInsight(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (title.trim()) fd.append("title", title.trim());
      const res = await api.post("/center/licensing/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data || {};
      // Only overwrite fields the user hasn't touched (so an explicit pick
      // from the dropdown isn't silently overridden by the AI).
      if (d.licensing_key && !preset.key && !licensingKey) setLicensingKey(d.licensing_key);
      if (d.expiration_date && !expires) setExpires(d.expiration_date);
      if (d.suggested_title && (!title || title === preset.label)) setTitle(d.suggested_title);
      setAiInsight({
        confidence: d.confidence || "medium",
        reasoning: d.reasoning || "",
        detected: d.licensing_key
          ? (checklist.find(x => x.key === d.licensing_key)?.label || d.licensing_key)
          : null,
        expires: d.expiration_date || null,
      });
    } catch (err) {
      // Silent fail — form stays usable, user just types manually.
      console.warn("licensing analyze failed", err);
    } finally { setAnalysing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Pick a file to upload.");
    if (!title.trim()) return toast.error("Add a short title.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      if (licensingKey) fd.append("licensing_key", licensingKey);
      if (expires) fd.append("expires_on", expires);
      if (notes.trim()) fd.append("notes", notes.trim());
      fd.append("file", f);
      await api.post("/center/licensing/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title} uploaded.`);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const confidenceTone = {
    high:   "bg-emerald-50 border-emerald-200 text-emerald-800",
    medium: "bg-amber-50  border-amber-200  text-amber-800",
    low:    "bg-stone-100 border-stone-300  text-stone-600",
  }[aiInsight?.confidence || "medium"] || "bg-stone-100 border-stone-300 text-stone-600";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="licensing-upload-form">
      <div>
        <Label>File</Label>
        <Input
          data-testid="licensing-form-file"
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          ref={fileRef}
          onChange={onFilePicked}
        />
        {analysing && (
          <div data-testid="licensing-analysing" className="mt-2 flex items-center gap-2 text-xs text-stone-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Reading document — auto-detecting type &amp; expiry…
          </div>
        )}
        {aiInsight && !analysing && (
          <div
            data-testid="licensing-ai-insight"
            className={`mt-2 rounded-lg border px-3 py-2 text-xs ${confidenceTone}`}
          >
            <div className="font-semibold uppercase tracking-wider text-[10px] flex items-center gap-1">
              <Info className="w-3 h-3" /> AI suggestion · {aiInsight.confidence} confidence
            </div>
            <div className="mt-1 leading-relaxed">
              {aiInsight.detected
                ? <>Detected: <strong>{aiInsight.detected}</strong>{aiInsight.expires && <> · expiring <strong>{fmtDate(aiInsight.expires)}</strong></>}</>
                : "Couldn't confidently classify this file — please pick the type below."}
              {aiInsight.reasoning && <div className="opacity-75 mt-0.5">{aiInsight.reasoning}</div>}
            </div>
          </div>
        )}
      </div>
      <div>
        <Label>Document type</Label>
        <Select value={licensingKey || "other"} onValueChange={(v) => setLicensingKey(v === "other" ? "" : v)}>
          <SelectTrigger data-testid="licensing-form-key"><SelectValue /></SelectTrigger>
          <SelectContent>
            {checklist.map(i => (
              <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>
            ))}
            <SelectItem value="other">Other (free-form)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Title</Label>
        <Input data-testid="licensing-form-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Fire Safety Inspection 2026" />
      </div>
      <div>
        <Label>Expires on</Label>
        <Input data-testid="licensing-form-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Textarea data-testid="licensing-form-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Passed with one corrective action re: EXIT sign" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" data-testid="licensing-form-submit" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" disabled={busy || analysing}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Upload
        </Button>
      </div>
    </form>
  );
}
